import { TimelineTimeframe } from '@wilson/interfaces';
import {
  differenceInCalendarDays,
  differenceInDays,
  differenceInMinutes,
  startOfDay,
} from 'date-fns';

export interface GanttActivityPixelCalculationInput {
  startTime: string | number;
  endTime: string | number;
  frameLength: number;
  dayCount: number;
  timeframe: TimelineTimeframe;
}

export function calculateStartingPixel({
  startTime,
  frameLength,
  dayCount,
  timeframe,
}: Omit<GanttActivityPixelCalculationInput, 'endTime'>): number {
  const startDate = new Date(startTime);
  const timelineStart = startOfDay(timeframe.start);

  if (startDate < timelineStart) {
    const timeDifference = differenceInMinutes(timelineStart, startDate);
    const timeMultiplier = frameLength / dayCount / 1440;
    return -1 * timeDifference * timeMultiplier; // Multiply by -1 to get the correct negative value
  } else {
    const dayOffset = differenceInDays(startDate, timelineStart);
    const timeOffset = differenceInMinutes(startDate, startOfDay(startDate));
    const dayMultiplier = frameLength / dayCount;
    const timeMultiplier = frameLength / dayCount / 1440;
    return dayOffset * dayMultiplier + timeOffset * timeMultiplier;
  }
}

export function calculateEndingPixel({
  startTime,
  endTime,
  frameLength,
  dayCount,
  timeframe,
}: GanttActivityPixelCalculationInput): number {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  const dayOffset = differenceInCalendarDays(
    startDate,
    startOfDay(timeframe.start),
  );
  const timeOffset = differenceInMinutes(startDate, startOfDay(startDate));

  const minutesInDay = 1440;
  const dayMultiplier = frameLength / dayCount;
  const timeMultiplier = frameLength / dayCount / minutesInDay;
  const duration = differenceInMinutes(endDate, startDate);
  return (
    frameLength -
    (dayOffset * dayMultiplier + timeOffset * timeMultiplier) -
    duration * timeMultiplier
  );
}
